<script lang="ts">
  import AddAdmin from "@frontend/Components/AddAdmin.svelte";
  import EditAdminForm from "@frontend/Components/EditAdminForm.svelte";
  import Modal from "@frontend/Components/Modal.svelte";
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import { service } from "../services";
  import { GetCachedToken, me } from "../user";
  import { AuthenticatedRequestOfDisableUserDTO, User, UserRole } from "../walletapi";
  import { admins, usersByRole, RefreshUsers } from "@frontend/userlist";
  import { RefreshGroups } from "@frontend/grouplist";
  import Pagination from "@frontend/Components/Pagination.svelte";
  import { get } from "svelte/store";
  import { groups } from "@frontend/grouplist";
  import { translate, currentLanguage } from "../Translator";

  onMount(() => {
    if (!$me) {
      push("/user/Login");
    }
  });

  onMount(async () => {
    await Promise.all([RefreshUsers(), RefreshGroups()]);
  });

  let message: { text: string; type: string } | null = null;

  // Función para verificar si un usuario pertenece a mi jerarquía de grupos
  function isUserInMyGroupHierarchy(user: User): boolean {
    // Si soy Owner, veo todos
    if ($me?.Role === UserRole.Owner) {
      return true;
    }

    // Si soy SuperAdmin (grupo 1), veo todos
    // if ($me?.GroupId === 1) {
    //   console.log('Soy SuperAdmin - mostrando todos los admins');
    //   return true;
    // }

    // Si el usuario no tiene grupo, no lo muestro
    if (!user.GroupId || !$me?.GroupId) {
      return false;
    }

    // Convertir IDs a números para comparación consistente
    const userGroupId = Number(user.GroupId);
    const myGroupId = Number($me.GroupId);

    // 1. Está en mi grupo
    if (userGroupId === myGroupId) {
      return true;
    }

    // 2. Está en un grupo hijo directo
    if (user.Group?.ParentId && Number(user.Group.ParentId) === myGroupId) {
      return true;
    }

    // 3. Está en un grupo nieto o más profundo
    let currentGroup = user.Group;
    while (currentGroup?.ParentId) {
      if (Number(currentGroup.ParentId) === myGroupId) {
        return true;
      }
      const parentId = Number(currentGroup.ParentId);
      currentGroup = get(groups).find(g => g.Id === parentId);
    }

    return false;
  }

  async function DisablePlayer(user: User) {
    try {
      // Actualizar el estado inmediatamente en la interfaz
      user.Enabled = !user.Enabled;
      
      const result = await service.walletapi.disableUser({
        Token: GetCachedToken(),
        Data: {
          Enabled: user.Enabled,
          Id: user.Id
        }
      });

      if (result.Success) {
        // Actualizar el usuario en el store
        const admins = get(usersByRole[UserRole.Admin]);
        const updatedAdmins = admins.map(p => 
          p.Id === user.Id ? { ...p, Enabled: user.Enabled } : p
        );
        usersByRole[UserRole.Admin].set(updatedAdmins);

        message = {
          text: `Admin ${user.FirstName} has been ${user.Enabled ? 'enabled' : 'disabled'} successfully`,
          type: user.Enabled ? 'success' : 'negative'
        };
      } else {
        // Si hay error, revertir el cambio
        user.Enabled = !user.Enabled;
        message = {
          text: result.Message || 'Error updating admin status',
          type: 'error'
        };
      }
    } catch (err) {
      // Si hay error, revertir el cambio
      user.Enabled = !user.Enabled;
      message = {
        text: err instanceof Error ? err.message : 'An unexpected error occurred',
        type: 'error'
      };
    }

    // Limpiar el mensaje después de 3 segundos
    setTimeout(() => {
      message = null;
    }, 3000);
  }

  type EditAction = {
    action: "edit",
    userid: number;
  };

  function dismiss() {
    push("#/AdminList");
  }

  type AddAction = {
    action: "add"
  };

  type RouteParams =
    EditAction |
    AddAction |
    undefined;
  
  export let params: RouteParams = undefined;

  let selectedUser: User | undefined = undefined;
  $: if (params) {
    if (params.action == "edit") {
      let selectedUserId = params.userid;
      selectedUser = get(admins).find(p => p.Id == selectedUserId);
    }
  }

  let allAdmins: User[] = [];
  let searchAdmins: User[] = [];
  let searchInput: string = "";
  let currentPage = 1;
  const pageSize = 20;
  $: visibleAdmins = searchAdmins.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Filtrar admins por grupo y búsqueda
  admins.subscribe((_admins) => {
    // Primero filtrar por grupo
    allAdmins = _admins.filter(isUserInMyGroupHierarchy);
    filterAdmins();
  });

  // Asegurarnos de que la lista se actualice cuando cambie el usuario
  $: if ($me) {
    admins.subscribe((_admins) => {
      allAdmins = _admins.filter(isUserInMyGroupHierarchy);
      filterAdmins();
    });
  }

  function filterAdmins() {
    currentPage = 1;
    if (searchInput == '') {
      searchAdmins = allAdmins;
    } else {
      let searchInputNormalized = searchInput.toLowerCase();
      searchAdmins = allAdmins.filter(p => 
        p.Email?.toLowerCase().includes(searchInputNormalized) ||
        p.FirstName?.toLowerCase().includes(searchInputNormalized) ||
        p.LastName?.toLowerCase().includes(searchInputNormalized)
      );
    }
  }

  function getGroupPath(group: any): string {
    if (!group || !$me?.GroupId) return group?.Name || '';
    
    const myGroupId = Number($me.GroupId);
    const userGroupId = Number(group.Id);
    
    // Si el usuario está en mi mismo grupo
    if (userGroupId === myGroupId) {
      return '📁';
    }
    
    // Construir la jerarquía completa del grupo del usuario (de arriba hacia abajo)
    let pathParts: string[] = [];
    let currentGroup: any = group;
    
    // Primero construir el array de grupos desde el usuario hasta la raíz
    while (currentGroup) {
      if (currentGroup.Name) {
        pathParts.unshift(currentGroup.Name);
      }
      if (currentGroup.ParentId) {
        currentGroup = get(groups).find(g => g.Id === currentGroup.ParentId);
      } else {
        break;
      }
    }
    
    // Buscar si mi grupo está en la jerarquía del usuario
    let myGroupIndex = -1;
    currentGroup = group;
    
    // Recorrer desde el grupo del usuario hacia arriba buscando mi grupo
    let index = pathParts.length - 1;
    while (currentGroup) {
      const currentGroupId = Number(currentGroup.Id);
      if (currentGroupId === myGroupId) {
        myGroupIndex = index;
        break;
      }
      if (currentGroup.ParentId) {
        currentGroup = get(groups).find(g => g.Id === currentGroup.ParentId);
        index--;
      } else {
        break;
      }
    }
    
    // Si mi grupo está en la jerarquía, reemplazar con "📁" y mantener los subgrupos
    if (myGroupIndex >= 0) {
      // Tomar solo desde mi grupo hacia abajo y reemplazar el nombre de mi grupo con "📁"
      const subPath = pathParts.slice(myGroupIndex);
      subPath[0] = '📁';
      return subPath.join(' └─ ');
    }
    
    // Si no hay relación, mostrar la jerarquía completa
    return pathParts.join(' └─ ');
  }
</script>

{#if params?.action == "add"}
    <Modal dismiss={dismiss} size="large">
      <AddAdmin dismiss={dismiss}></AddAdmin>
    </Modal>
{/if}

{#if params?.action == "edit"}
  <Modal dismiss={dismiss} size="large">
    {#if !selectedUser}
      <div class="ui negative message">
        <div class="header">No such Admin</div>
      </div>
    {:else}
      <EditAdminForm player={selectedUser} dismiss={dismiss}></EditAdminForm>
    {/if}
  </Modal>
{/if}

{#if message}
  <div class="ui {message.type} message">
    <p>{message.text}</p>
  </div>
{/if}

<h1 class="ui centered dividing header">{$translate('List of Managers')}</h1>
{#if $me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin}
  <a href="#/AdminList/add" class="ui green button">
    <i class="user plus icon"></i>
    {$translate('Add Admin')}
  </a>
{/if}

<table class="playerlist ui celled unstackable striped selectable very compact unselectable table">
  <thead>
    <tr>
      <th style="width: 0px">#</th>
      <th>{$translate('First Name')}</th>
      <th>{$translate('Last Name')}</th>
      <th>{$translate('E-mail')}</th>
      <th>{$translate('Phone Number')}</th>
      <th>{$translate('Group')}</th>
      <th>{ $translate('Referral')}</th>
      {#if $me?.Role === UserRole.Owner}
        <th style="width: 0px">Allow</th>
        <th style="width: 0px">{$translate('Options')}</th>
      {/if}
    </tr>
  </thead>
  <tbody>
    {#each visibleAdmins ?? [] as user}
      <tr>
        <td class="numeric">{user.Id}</td>
        <td>{user.FirstName ?? ""}</td>
        <td>{user.LastName ?? ""}</td>
        <td>{user.Email ?? ""}</td>
        <td>{user.PhoneNumber ?? ""}</td>
        <td class="group-column" title={getGroupPath(user.Group)}>
          <span class="my-group-badge">{getGroupPath(user.Group)}</span>
        </td>
        <td>{user.Reference ?? ""}</td>
        {#if $me?.Role === UserRole.Owner}
          <td>
            <button class={`ui icon button ${user.Enabled ? 'positive' : 'negative'}`} on:click={() => DisablePlayer(user)}>
              {#if user.Enabled}
                <i class="check icon"></i>
              {:else}
                <i class="close icon"></i>
              {/if}
            </button>
          </td>
          <td style="white-space: nowrap;">
            <a class="ui primary icon small button" href="#/administratorlist/edit/{user.Id}">
              <i class="edit icon"></i>
            </a>
          </td>
        {/if}
      </tr>
    {/each}     
  </tbody>
</table>
<Pagination page={currentPage} totalItems={searchAdmins.length} {pageSize} on:change={(event) => currentPage = event.detail} />

<style>
  .center {
    text-align: center;
  }
  .hidden {
    display: none;
  }
  .unselectable {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  .numeric {
    font-family: monospace;
    text-align: right !important;
  }
  .ui.message {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    margin: 0;
    padding: 1em 2em;
    border-radius: 4px;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    min-width: 300px;
    text-align: center;
  }

  .ui.success.message {
    background-color: #21ba45;
    color: white;
  }

  .ui.negative.message,
  .ui.error.message {
    background-color: #db2828;
    color: white;
  }

  .group-column {
    width: 180px !important;
    text-align: left !important;
    max-width: 180px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .my-group-badge {
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: bold;
    border: 1px solid #2196f3;
  }
</style>
